import { useEffect, useState } from 'react';
import { Get, Post, Send } from '../utils/RemoteAuthority';
import { RefreshLobby, SetTitle } from '../utils/Lobby';
import TopBar from './TopBar';
import { useStaticButtons } from '../utils/Preferences';
import './Lobby.css';

const NoAnswer = { ok: false, error: 'The game server is not answering.' };

// Your profile: the name you go by (in your games too) and, with a password kept here, a new password. Your username
// stays as it is. Signed in another way (e.g. Tailscale), your name comes from there and is only shown.
function Profile()
{
    const [me, setMe] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        SetTitle('Profile · Through the Ages');
        Get('/games/me').then(result => result.ok === false ? setError(result.error) : setMe(result)).catch(() => setError(NoAnswer.error));
    }, []);

    return <div className="tta-lobby">
        <TopBar page="profile"/>
        <main className="tta-lobby-main">
            <h1 className="tta-lobby-heading">Profile</h1>
            {error && <div className="tta-lobby-error">{error}</div>}
            {!me && !error && <div className="tta-muted">Loading…</div>}
            {me && <>
                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Name</h2>
                    <div className="tta-panel tta-profile-panel">
                        {me.editableName ? <NameForm me={me} OnSaved={(name) => setMe({ ...me, name })}/>
                            : <p>{me.name} <span className="tta-muted">(from the way you sign in)</span></p>}
                        <div className="tta-muted tta-field-note">Username {me.login}: you sign in with it, and it can't be changed.</div>
                    </div>
                </section>
                {me.form && <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Password</h2>
                    <div className="tta-panel tta-profile-panel"><PasswordForm/></div>
                </section>}
                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Playing</h2>
                    <div className="tta-panel tta-profile-panel"><PlayingSettings/></div>
                </section>
            </>}
        </main>
    </div>;
}

// The name you go by: everyone sees it, in every game you are in, the ones already started too
function NameForm({ me, OnSaved })
{
    const [name, setName] = useState(me.name);
    const [note, setNote] = useState(null);
    const [error, setError] = useState(null);
    const changed = name.trim() !== '' && name.trim().replace(/\s+/g, ' ') !== me.name;
    const save = async () => {
        const result = await Send('PUT', '/games/me', { name }).catch(() => NoAnswer);
        if (result.ok === false) return setError(result.error);
        setError(null);
        setName(result.name);
        setNote('Saved. Games open in other tabs show it once they are reloaded.');
        OnSaved(result.name);
        RefreshLobby();
    };
    return <>
        <label className="tta-field">
            <span className="tta-field-label">The name you go by</span>
            <div className="tta-field-row">
                <input className="tta-input" value={name} maxLength={40}
                    onChange={(e) => { setName(e.target.value); setNote(null); }}
                    onKeyDown={(e) => e.key === 'Enter' && changed && save()}/>
                <button className="tta-primary-button" disabled={!changed} onClick={save}>Save</button>
            </div>
            <span className="tta-muted tta-field-note">Everyone sees it, in all your games, the ones already started too.</span>
        </label>
        {note && <div className="tta-admin-note">{note}</div>}
        {error && <div className="tta-lobby-error">{error}</div>}
    </>;
}

// A new password, given the one now; every other browser you are signed in on is signed out
function PasswordForm()
{
    const [current, setCurrent] = useState('');
    const [password, setPassword] = useState('');
    const [repeated, setRepeated] = useState('');
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState(null);
    const [error, setError] = useState(null);
    const mismatch = repeated !== '' && repeated !== password;
    const ready = current !== '' && password.length >= 8 && password === repeated && !busy;
    const save = async () => {
        setBusy(true);
        const result = await Post('/auth/password/change', { current, password }).catch(() => NoAnswer);
        setBusy(false);
        if (result.ok === false) return setError(result.error);
        setError(null);
        setCurrent(''); setPassword(''); setRepeated('');
        setNote('Saved. Any other browsers you were signed in on are signed out.');
    };
    const field = (label, value, set, autoComplete, note) => <label className="tta-field">
        <span className="tta-field-label">{label}</span>
        <input className="tta-input" type="password" value={value} autoComplete={autoComplete}
            onChange={(e) => { set(e.target.value); setNote(null); }}/>
        {note && <span className="tta-muted tta-field-note">{note}</span>}
    </label>;
    return <form onSubmit={(e) => { e.preventDefault(); if (ready) save(); }}>
        {field('Your password now', current, setCurrent, 'current-password')}
        {field('New password', password, setPassword, 'new-password', 'At least 8 characters.')}
        {field('New password again', repeated, setRepeated, 'new-password', mismatch ? 'Not the same as the new password.' : null)}
        <button className="tta-primary-button" type="submit" disabled={!ready}>Change password</button>
        {note && <div className="tta-admin-note">{note}</div>}
        {error && <div className="tta-lobby-error">{error}</div>}
    </form>;
}

// How your games are shown, kept in this browser (see Preferences.js)
function PlayingSettings()
{
    const [staticButtons, setStaticButtons] = useStaticButtons();
    return <div className="tta-field">
        <label className="tta-switch">
            <input type="checkbox" checked={staticButtons} onChange={(e) => setStaticButtons(e.target.checked)}/>
            <span>Use static buttons</span>
        </label>
        <span className="tta-muted tta-field-note">
            A fixed button for each kind of action under your board (Build, Upgrade, Develop…): click one, then pick what to do
            it with on your board or in your hand. In place of the action panel's buttons for those actions. Kept in this
            browser.
        </span>
    </div>;
}

export default Profile;
